export const STALE_CHUNK_RELOADS_KEY = 'staleChunkReloads';

// Used when the error does not name the chunk. Safari/WebKit reports a failed dynamic import as
// "Importing a module script failed." with no URL, so these failures are keyed by the build the page
// is running instead: one reload per deploy, and still no loop within a build.
export const UNKNOWN_CHUNK = 'unknown';

interface ReloadOnStaleChunkDeps {
  target: EventTarget;
  storage: Pick<Storage, 'getItem' | 'setItem'>;
  isOnline: () => boolean;
  reload: () => void;
  log: (message: string) => void;
  // Identifies the build this page loaded, e.g. the entry script path, which changes on every deploy.
  currentBuild: () => string;
}

type PreloadErrorEvent = Event & { payload?: unknown };

// Chromium and Firefox name the module that failed, e.g. "Failed to fetch dynamically imported module:
// https://host/assets/LoginPage-AbC123.js", and Vite does for CSS: "Unable to preload CSS for
// /assets/LoginPage-AbC123.css". WebKit does not ("Importing a module script failed.").
export const chunkFromPreloadError = (payload: unknown): string => {
  const message = payload instanceof Error ? payload.message : String(payload ?? '');
  return message.match(/\/assets\/[^\s'"?#]+/)?.[0] ?? UNKNOWN_CHUNK;
};

// null means storage itself is unusable; a corrupt record reads as empty and is overwritten.
const readReloadedChunks = (storage: ReloadOnStaleChunkDeps['storage']): string[] | null => {
  let value: string | null;
  try {
    value = storage.getItem(STALE_CHUNK_RELOADS_KEY);
  } catch {
    return null;
  }
  try {
    const parsed: unknown = value === null ? [] : JSON.parse(value);
    return Array.isArray(parsed)
      ? parsed.filter((item): item is string => typeof item === 'string')
      : [];
  } catch {
    return [];
  }
};

// The record has to survive the reload; if it cannot be saved, nothing would stop a loop.
const writeReloadedChunks = (
  storage: ReloadOnStaleChunkDeps['storage'],
  chunks: string[]
): boolean => {
  try {
    storage.setItem(STALE_CHUNK_RELOADS_KEY, JSON.stringify(chunks));
    return true;
  } catch {
    return false;
  }
};

/**
 * After a deploy, a tab that loaded the previous index.html can request a lazy
 * chunk that no longer exists. Vite reports that as `vite:preloadError`; a reload
 * fetches the current index.html and its chunks, so the user never has to clear
 * their cache.
 *
 * Each chunk gets at most one reload per tab session. If the same chunk fails again
 * (it is broken, or the connection is flaky), the error is left to surface instead of
 * reloading every time the user comes back to it. A later deploy's chunks have new
 * hashed names, so it can still recover with one reload. Failures that name no chunk
 * get one reload per build instead.
 */
export const installReloadOnStaleChunk = ({
  target,
  storage,
  isOnline,
  reload,
  log,
  currentBuild,
}: ReloadOnStaleChunkDeps) => {
  target.addEventListener('vite:preloadError', (event) => {
    if (!isOnline()) {
      return;
    }

    const reloaded = readReloadedChunks(storage);
    if (reloaded === null) {
      return;
    }

    const named = chunkFromPreloadError((event as PreloadErrorEvent).payload);
    const chunk = named === UNKNOWN_CHUNK ? `${UNKNOWN_CHUNK}@${currentBuild()}` : named;
    if (reloaded.includes(chunk)) {
      return;
    }

    if (!writeReloadedChunks(storage, [...reloaded, chunk])) {
      return;
    }

    event.preventDefault();
    log(`A lazy chunk failed to load, probably after a deploy. Reloading. (${chunk})`);
    reload();
  });
};
